import { useState } from 'react';
import { Box, Text, as } from 'folds';
import { sizedIcon, userFallbackIcon, Warning } from '$components/icons/phosphor';
import classNames from 'classnames';
import type { MatrixClient, MatrixEvent, Room } from '$types/matrix-sdk';
import { useAtomValue } from 'jotai';
import { nicknamesAtom } from '$state/nicknames';
import { Avatar, AvatarShape } from '$components/avatar';
import { UserAvatar } from '$components/user-avatar';
import { getMemberAvatarMxc, getMemberDisplayName } from '$utils/room/display';
import { getMxIdLocalPart, mxcUrlToHttp } from '$utils/matrix';
import { useRenderableMediaUrl } from '$hooks/useRenderableMediaUrl';
import { isJumboEmojiText } from '$utils/emojiDetection';
import { ReactionMedia } from './ReactionMedia';
import * as css from './Reaction.css';

export const Reaction = as<
  'button',
  {
    mx: MatrixClient;
    count: number;
    reaction: string;
    useAuthentication?: boolean;
  }
>(({ className, mx, count, reaction, useAuthentication, ...props }, ref) => {
  const [imgError, setImgError] = useState(false);
  const isCustom = reaction.startsWith('mxc://');
  const isEmojiKey = isCustom || isJumboEmojiText(reaction);
  const showCount = count > 1;
  const rawUrl = isCustom
    ? (mxcUrlToHttp(mx, reaction, useAuthentication) ?? undefined)
    : undefined;
  const renderableUrl = useRenderableMediaUrl(rawUrl);

  const customEmojiImg =
    imgError || !renderableUrl ? (
      <span title="Failed to load emoji image" aria-label="Failed to load emoji image">
        {sizedIcon(Warning, '100', { style: { opacity: 0.5 } })}
      </span>
    ) : (
      <ReactionMedia src={renderableUrl}>
        <img
          className={showCount ? css.ReactionChipImg : css.ReactionSoloImgCustom}
          src={renderableUrl}
          alt={reaction}
          onError={() => setImgError(true)}
        />
      </ReactionMedia>
    );

  let body;
  if (!isEmojiKey) {
    body = (
      <span
        className={classNames(
          css.ReactionPill,
          css.ReactionPillText,
          !showCount && css.ReactionPillTextSolo
        )}
      >
        <Text className={css.ReactionKey} as="span" size="T300" truncate>
          {reaction}
        </Text>
        {showCount && (
          <Text className={css.ReactionCount} as="span" size="T300">
            {count}
          </Text>
        )}
      </span>
    );
  } else if (showCount) {
    body = (
      <>
        <span className={classNames(css.ReactionText, isCustom && css.ReactionTextCustom)}>
          {isCustom ? customEmojiImg : reaction}
        </span>
        <span className={css.ReactionPill}>
          <Text className={css.ReactionCount} as="span" size="T300">
            {count}
          </Text>
        </span>
      </>
    );
  } else {
    body = (
      <span className={css.ReactionSolo}>
        {isCustom ? customEmojiImg : <span className={css.ReactionSoloEmoji}>{reaction}</span>}
      </span>
    );
  }

  return (
    <Box
      as="button"
      className={classNames(css.Reaction, !isEmojiKey && css.ReactionTextMode, className)}
      alignItems="Center"
      shrink="No"
      {...props}
      ref={ref}
    >
      {body}
    </Box>
  );
});

type ReactionTooltipMsgProps = {
  room: Room;
  events: MatrixEvent[];
  mx: MatrixClient;
  useAuthentication?: boolean;
};

function ReactionTooltipPersonRow({
  room,
  ev,
  mx,
  useAuthentication,
}: {
  room: Room;
  ev: MatrixEvent;
  mx: MatrixClient;
  useAuthentication?: boolean;
}) {
  const nicknames = useAtomValue(nicknamesAtom);
  const senderId = ev.getSender();
  if (!senderId) return null;

  const name =
    getMemberDisplayName(room, senderId, nicknames) ?? getMxIdLocalPart(senderId) ?? senderId;

  const avatarMxc = getMemberAvatarMxc(room, senderId);
  const avatarUrl = avatarMxc
    ? (mxcUrlToHttp(mx, avatarMxc, useAuthentication, 64, 64, 'crop') ?? undefined)
    : undefined;

  return (
    <Box className={css.ReactionTooltipPerson} alignItems="Center" gap="200">
      <Box shrink="No">
        <Avatar shape={AvatarShape.Circle} size="200">
          <UserAvatar
            userId={senderId}
            src={avatarUrl}
            alt={name}
            renderFallback={() => userFallbackIcon('sm')}
          />
        </Avatar>
      </Box>
      <Box grow="Yes" style={{ minWidth: 0 }}>
        <Text size="T300" truncate>
          {name}
        </Text>
      </Box>
    </Box>
  );
}

const MAX_TOOLTIP_REACTORS = 3;

export function ReactionTooltipMsg({
  room,
  events,
  mx,
  useAuthentication,
}: ReactionTooltipMsgProps) {
  const withSender = events.filter((ev) => ev.getSender());
  const shown = withSender.slice(0, MAX_TOOLTIP_REACTORS);
  const othersCount = withSender.length - MAX_TOOLTIP_REACTORS;

  return (
    <Box className={css.ReactionTooltipList} direction="Column">
      {shown.map((ev) => (
        <ReactionTooltipPersonRow
          key={ev.getId()}
          room={room}
          ev={ev}
          mx={mx}
          useAuthentication={useAuthentication}
        />
      ))}
      {othersCount > 0 && (
        <Box className={css.ReactionTooltipPerson} alignItems="Center" gap="200">
          <Box shrink="No" aria-hidden className={css.ReactionTooltipAvatarSpacer} />
          <Text as="span" size="T300" priority="300">
            and <b>{othersCount} others</b>
          </Text>
        </Box>
      )}
    </Box>
  );
}
